// frontend/src/pages/member/MyProfilePage.jsx
import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import MemberLayout from './MemberLayout';

const API_URL = process.env.REACT_APP_API_URL || 'http://localhost:5000/api';

const MyProfilePage = () => {
  const navigate = useNavigate();

  const [member, setMember] = useState(null);
  const [balances, setBalances] = useState({
    shares: 0,
    normalLoan: 0,
    schoolLoan: 0,
    emergencyLoan: 0
  });

  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState('');

  // ✅ NEW: edit-once controls
  const [isEditing, setIsEditing] = useState(false);
  const [isSaving, setIsSaving] = useState(false);
  const [success, setSuccess] = useState('');

  const [memberLoginId, setMemberLoginId] = useState(''); // memberId from logged-in profile (for local lock key)
  const [formData, setFormData] = useState({
    personalInfo: {
      fullName: '',
      idNumber: '',
      phoneNumber: '',
      email: '',
      dateOfBirth: '',
      physicalAddress: '',
      gender: '',
      city: '',
      homeCounty: '',
      employmentStatus: '',
      employerName: ''
    },
    nextOfKin: {
      name: '',
      phoneNumber: '',
      relationship: '',
      idNumber: '',
      physicalAddress: '',
      benefitPercentage: ''
    }
  });

  const [otherNextOfKin, setOtherNextOfKin] = useState([]);

  const formatDate = (dateValue) => {
    if (!dateValue) return 'Unknown';
    try {
      let date;
      if (dateValue.toDate && typeof dateValue.toDate === 'function') {
        date = dateValue.toDate();
      } else if (dateValue._seconds) {
        date = new Date(dateValue._seconds * 1000);
      } else {
        date = new Date(dateValue);
      }

      if (isNaN(date.getTime())) return 'Unknown';

      return date.toLocaleDateString('en-GB', {
        day: '2-digit',
        month: 'short',
        year: 'numeric'
      });
    } catch {
      return 'Unknown';
    }
  };

  // ✅ NEW: format to YYYY-MM-DD for <input type="date" />
  const formatDateForInput = (dateValue) => {
    if (!dateValue) return '';
    try {
      let date;
      if (dateValue.toDate && typeof dateValue.toDate === 'function') {
        date = dateValue.toDate();
      } else if (dateValue._seconds) {
        date = new Date(dateValue._seconds * 1000);
      } else {
        date = new Date(dateValue);
      }
      if (isNaN(date.getTime())) return '';
      return date.toISOString().split('T')[0];
    } catch {
      return '';
    }
  };

  useEffect(() => {
    fetchMemberData();
  }, []);

  const fetchMemberData = async () => {
    try {
      const token = localStorage.getItem('hopeSaccoToken');
      const storedUser = localStorage.getItem('hopeSaccoUser');

      if (!token || !storedUser) {
        navigate('/login');
        return;
      }

      const userData = JSON.parse(storedUser);

      // Allow member, admin, and official roles
      const allowedRoles = ['member', 'admin', 'official'];
      if (!allowedRoles.includes(userData.role)) {
        navigate('/login');
        return;
      }

      const memberId = userData.profile?.memberId;

      if (!memberId) {
        setError('Member ID not found');
        setIsLoading(false);
        return;
      }

      setMemberLoginId(memberId);

      // Fetch member profile
      const memberResponse = await fetch(`${API_URL}/members/by-member-id/${memberId}/profile`, {
        headers: { 'Authorization': `Bearer ${token}` }
      });

      if (!memberResponse.ok) throw new Error('Failed to fetch member profile');

      const memberData = await memberResponse.json();

      if (!memberData.success) {
        throw new Error(memberData.message || 'Failed to load profile');
      }

      const m = memberData.data.member;
      setMember(m);

      // Prime edit form with current profile (✅ NEW)
      setFormData({
        personalInfo: {
          fullName: m.personalInfo?.fullName || '',
          idNumber: m.personalInfo?.idNumber || '',
          phoneNumber: m.personalInfo?.phoneNumber || '',
          email: m.personalInfo?.email || '',
          dateOfBirth: formatDateForInput(m.personalInfo?.dateOfBirth),
          physicalAddress: m.personalInfo?.physicalAddress || '',
          gender: m.personalInfo?.gender || '',
          city: m.personalInfo?.city || '',
          homeCounty: m.personalInfo?.homeCounty || '',
          employmentStatus: m.personalInfo?.employmentStatus || '',
          employerName: m.personalInfo?.employerName || ''
        },
        nextOfKin: {
          name: m.nextOfKin?.name || '',
          phoneNumber: m.nextOfKin?.phoneNumber || '',
          relationship: m.nextOfKin?.relationship || '',
          idNumber: m.nextOfKin?.idNumber || '',
          physicalAddress: m.nextOfKin?.physicalAddress || '',
          benefitPercentage: m.nextOfKin?.benefitPercentage ?? ''
        }
      });

      // Populate other next of kin if present
      if (Array.isArray(m.otherNextOfKin) && m.otherNextOfKin.length > 0) {
        setOtherNextOfKin(m.otherNextOfKin.map(k => ({
          name: k.name || '',
          relationship: k.relationship || '',
          benefitPercentage: k.benefitPercentage ?? ''
        })));
      }

      // Fetch balances from monthly overview
      await fetchBalancesFromMonthlyOverview(memberId, token);
    } catch (error) {
      console.error('Error:', error);
      setError('Failed to load profile data');
    } finally {
      setIsLoading(false);
    }
  };

  const fetchBalancesFromMonthlyOverview = async (memberId, token) => {
    try {
      const now = new Date();
      // Use previous month — current month contributions are not yet made
      const prev = new Date(now.getFullYear(), now.getMonth() - 1, 1);
      const currentMonth = `${prev.getFullYear()}-${String(prev.getMonth() + 1).padStart(2, '0')}`;

      const response = await fetch(
        `${API_URL}/reports/monthly-overview?month=${currentMonth}`,
        { headers: { 'Authorization': `Bearer ${token}` } }
      );

      if (response.ok) {
        const data = await response.json();

        if (data.success && data.data && data.data.members) {
          const memberData = data.data.members.find(m => m.memberId === memberId);

          if (memberData && memberData.monthData) {
            const md = memberData.monthData;
            setBalances({
              shares: md.shares || 0,
              normalLoan: md.loanBalance || 0,
              schoolLoan: md.schoolFeesLoan || 0,
              emergencyLoan: md.emergencyLoan || 0
            });
          } else {
            setBalancesFromMemberDocument();
          }
        } else {
          setBalancesFromMemberDocument();
        }
      } else {
        setBalancesFromMemberDocument();
      }
    } catch (error) {
      console.error('Error fetching balances:', error);
      setBalancesFromMemberDocument();
    }
  };

  const setBalancesFromMemberDocument = () => {
    if (!member) return;

    const cb = member.currentBalances || {};
    setBalances({
      shares: cb.shares || 0,
      normalLoan: cb.normalLoanBalance || cb.normalLoan || 0,
      schoolLoan: cb.schoolLoanBalance || cb.schoolLoan || cb.schoolFeesLoan || 0,
      emergencyLoan: cb.emergencyLoanBalance || cb.emergencyLoan || 0
    });
  };

  // ✅ NEW: determine if member has already edited ONCE (server flag OR local fallback)
  const getEditOnceLock = () => {
    const localKey = memberLoginId ? `hopeSaccoProfileEditedOnce_${memberLoginId}` : '';
    const localLocked = localKey ? localStorage.getItem(localKey) === 'true' : false;

    // Try common server-side flags (won’t break if not present)
    const serverLocked =
      member?.selfServiceEditOnce?.locked === true ||
      member?.selfServiceEditOnce?.completed === true ||
      member?.profileEditOnce?.locked === true ||
      member?.profileEditOnce?.completed === true ||
      member?.memberPortal?.profileLocked === true ||
      member?.memberPortal?.profileEditedOnce === true ||
      member?.meta?.profileEditedOnce === true ||
      member?.meta?.profileLocked === true;

    return Boolean(serverLocked || localLocked);
  };

  const profileLocked = getEditOnceLock();

  // ✅ NEW: input handler (same idea as admin edit page)
  const handleInputChange = (section, field, value) => {
    setFormData(prev => ({
      ...prev,
      [section]: {
        ...prev[section],
        [field]: value
      }
    }));
  };

  // Other Next of Kin handlers
  const addOtherKin = () => setOtherNextOfKin(prev => [...prev, { name: '', relationship: '', benefitPercentage: '' }]);
  const removeOtherKin = (idx) => setOtherNextOfKin(prev => prev.filter((_, i) => i !== idx));
  const handleOtherKinChange = (idx, field, value) => {
    setOtherNextOfKin(prev => prev.map((item, i) => i === idx ? { ...item, [field]: value } : item));
  };

  // ✅ NEW: save profile (allowed ONLY ONCE; fullName + memberId never editable)
  const handleSaveProfile = async () => {
    if (profileLocked) return;

    setIsSaving(true);
    setError('');
    setSuccess('');

    try {
      const token = localStorage.getItem('hopeSaccoToken');
      const storedUser = localStorage.getItem('hopeSaccoUser');

      if (!token || !storedUser) {
        navigate('/login');
        return;
      }

      const userData = JSON.parse(storedUser);
      const memberId = userData.profile?.memberId;

      if (!memberId) {
        setError('Member ID not found');
        setIsSaving(false);
        return;
      }

      // Build payload:
      // - fullName is NOT editable (kept from existing member data)
      // - memberId is NOT editable (not sent)
      const payload = {
        personalInfo: {
          ...formData.personalInfo,
          fullName: member?.personalInfo?.fullName || formData.personalInfo.fullName || ''
        },
        nextOfKin: {
          ...formData.nextOfKin
        },
        otherNextOfKin: otherNextOfKin.filter(k => k.name.trim()),
        // Ask backend to lock future edits (harmless if backend ignores)
        selfServiceEditOnce: {
          completed: true,
          locked: true,
          updatedAt: Date.now()
        }
      };

      // PUT to the member-profile endpoint (same “by-member-id” path as GET)
      const response = await fetch(`${API_URL}/members/by-member-id/${memberId}/profile`, {
        method: 'PUT',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify(payload)
      });

      if (!response.ok) throw new Error('Failed to update profile');

      const data = await response.json();
      if (!data.success) throw new Error(data.message || 'Failed to update profile');

      // Update local member state with returned member if present, else merge
      const updatedMember = data.data?.member
        ? data.data.member
        : {
            ...member,
            personalInfo: {
              ...(member?.personalInfo || {}),
              ...payload.personalInfo
            },
            nextOfKin: {
              ...(member?.nextOfKin || {}),
              ...payload.nextOfKin
            },
            selfServiceEditOnce: {
              ...(member?.selfServiceEditOnce || {}),
              completed: true,
              locked: true,
              updatedAt: Date.now()
            }
          };

      setMember(updatedMember);

      // Local hard lock fallback (even if backend does not store the flag)
      if (memberLoginId) {
        localStorage.setItem(`hopeSaccoProfileEditedOnce_${memberLoginId}`, 'true');
      }

      setSuccess('Profile updated successfully. Further edits are now locked (except name and Member ID).');
      setIsEditing(false);
    } catch (e) {
      console.error('Save profile error:', e);
      setError('Failed to update profile. Please try again.');
    } finally {
      setIsSaving(false);
    }
  };

  if (isLoading) {
    return (
      <MemberLayout activePage="profile" pageTitle="My Profile">
        <div className="loading-state">
          <div className="spinner-large"></div>
          <p>Loading your profile...</p>
        </div>
      </MemberLayout>
    );
  }

  if (error || !member) {
    return (
      <MemberLayout activePage="profile" pageTitle="My Profile">
        <div className="error-page">
          <h2>Error</h2>
          <p>{error || 'Member profile not found'}</p>
          <button className="btn btn-secondary" onClick={() => navigate('/member/dashboard')}>
            Back to Dashboard
          </button>
        </div>
      </MemberLayout>
    );
  }

  return (
    <MemberLayout
      activePage="profile"
      pageTitle="My Profile"
      breadcrumbs={[
        { label: 'Dashboard', link: '/member/dashboard' },
        { label: 'My Profile' }
      ]}
    >
      <div className="member-profile-page">
        {/* Header - Simplified for members */}
        <div className="profile-header">
          <div className="profile-title">
            <h1>{member.personalInfo?.fullName || 'Unknown Member'}</h1>
            <span className={`status-badge status-${member.saccoInfo?.status?.toLowerCase()}`}>
              {member.saccoInfo?.status || 'Unknown'}
            </span>
          </div>

          {/* ✅ NEW: Edit controls (edit allowed once; name + memberId never editable) */}
          <div style={{ display: 'flex', gap: '0.75rem', alignItems: 'center' }}>
            {success && (
              <span style={{ color: '#28a745', fontWeight: 600 }}>
                ✓ {success}
              </span>
            )}
            {!profileLocked && !isEditing && (
              <button className="btn btn-primary" onClick={() => { setSuccess(''); setError(''); setIsEditing(true); }}>
                Edit Profile (One Time)
              </button>
            )}
            {profileLocked && (
              <span style={{ color: '#666', fontWeight: 600 }}>
                Profile editing locked (one-time update used)
              </span>
            )}
            {isEditing && (
              <>
                <button className="btn btn-secondary" onClick={() => { setIsEditing(false); setError(''); setSuccess(''); }}>
                  Cancel
                </button>
                <button className="btn btn-primary" disabled={isSaving} onClick={handleSaveProfile}>
                  {isSaving ? 'Saving...' : 'Save (Final)'}
                </button>
              </>
            )}
          </div>
        </div>

        {error && (
          <div className="alert alert-error" style={{ marginTop: '1rem' }}>
            ⚠️ {error}
          </div>
        )}

        {/* Member ID Card */}
        <div className="member-card">
          <div className="card-info">
            <div className="info-item">
              <span className="label">Member ID:</span>
              <span className="value">{member.memberId}</span>
            </div>
            <div className="info-item">
              <span className="label">Phone:</span>
              <span className="value">{member.personalInfo?.phoneNumber || 'Not provided'}</span>
            </div>
            <div className="info-item">
              <span className="label">Email:</span>
              <span className="value">{member.personalInfo?.email || 'Not provided'}</span>
            </div>
            <div className="info-item">
              <span className="label">Joined:</span>
              <span className="value">{formatDate(member.saccoInfo?.dateJoined)}</span>
            </div>
          </div>
        </div>

        {/* Balance Summary Cards */}
        <div className="balance-cards">
          <div className="balance-card shares">
            <div className="card-icon">💰</div>
            <div className="card-content">
              <div className="card-label">Share Balance</div>
              <div className="card-value">
                KES {balances.shares.toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
              </div>
            </div>
          </div>
          <div className="balance-card loan normal">
            <div className="card-icon">🏦</div>
            <div className="card-content">
              <div className="card-label">Normal Loan</div>
              <div className="card-value">
                KES {balances.normalLoan.toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
              </div>
            </div>
          </div>
          <div className="balance-card loan school">
            <div className="card-icon">🎓</div>
            <div className="card-content">
              <div className="card-label">School Loan</div>
              <div className="card-value">
                KES {balances.schoolLoan.toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
              </div>
            </div>
          </div>
          <div className="balance-card loan emergency">
            <div className="card-icon">🚨</div>
            <div className="card-content">
              <div className="card-label">Emergency Loan</div>
              <div className="card-value">
                KES {balances.emergencyLoan.toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
              </div>
            </div>
          </div>
        </div>

        {/* Overview Section */}
        <div className="overview-container">
          <div className="info-sections">
            {/* Personal Information */}
            <div className="info-section">
              <h3>Personal Information</h3>
              <div className="info-grid">
                <div className="info-row">
                  <span className="info-label">Full Name:</span>
                  {/* ❌ Name never editable */}
                  <span className="info-value">{member.personalInfo?.fullName || 'N/A'}</span>
                </div>

                <div className="info-row">
                  <span className="info-label">ID Number:</span>
                  <span className="info-value">
                    {isEditing ? (
                      <input
                        type="text"
                        value={formData.personalInfo.idNumber}
                        onChange={(e) => handleInputChange('personalInfo', 'idNumber', e.target.value)}
                        style={{ width: '100%' }}
                      />
                    ) : (
                      (member.personalInfo?.idNumber || 'N/A')
                    )}
                  </span>
                </div>

                <div className="info-row">
                  <span className="info-label">Phone Number:</span>
                  <span className="info-value">
                    {isEditing ? (
                      <input
                        type="tel"
                        value={formData.personalInfo.phoneNumber}
                        onChange={(e) => handleInputChange('personalInfo', 'phoneNumber', e.target.value)}
                        style={{ width: '100%' }}
                      />
                    ) : (
                      (member.personalInfo?.phoneNumber || 'N/A')
                    )}
                  </span>
                </div>

                <div className="info-row">
                  <span className="info-label">Email:</span>
                  <span className="info-value">
                    {isEditing ? (
                      <input
                        type="email"
                        value={formData.personalInfo.email}
                        onChange={(e) => handleInputChange('personalInfo', 'email', e.target.value)}
                        style={{ width: '100%' }}
                      />
                    ) : (
                      (member.personalInfo?.email || 'N/A')
                    )}
                  </span>
                </div>

                <div className="info-row">
                  <span className="info-label">Date of Birth:</span>
                  <span className="info-value">
                    {isEditing ? (
                      <input
                        type="date"
                        value={formData.personalInfo.dateOfBirth}
                        onChange={(e) => handleInputChange('personalInfo', 'dateOfBirth', e.target.value)}
                        style={{ width: '100%' }}
                      />
                    ) : (
                      formatDate(member.personalInfo?.dateOfBirth)
                    )}
                  </span>
                </div>

                <div className="info-row">
                  <span className="info-label">Physical Address:</span>
                  <span className="info-value">
                    {isEditing ? (
                      <input
                        type="text"
                        value={formData.personalInfo.physicalAddress}
                        onChange={(e) => handleInputChange('personalInfo', 'physicalAddress', e.target.value)}
                        style={{ width: '100%' }}
                      />
                    ) : (
                      (member.personalInfo?.physicalAddress || 'N/A')
                    )}
                  </span>
                </div>

                <div className="info-row">
                  <span className="info-label">Gender:</span>
                  <span className="info-value">
                    {isEditing ? (
                      <select value={formData.personalInfo.gender} onChange={(e) => handleInputChange('personalInfo', 'gender', e.target.value)} style={{ width: '100%' }}>
                        <option value="">Select</option>
                        <option value="Male">Male</option>
                        <option value="Female">Female</option>
                      </select>
                    ) : (member.personalInfo?.gender || 'N/A')}
                  </span>
                </div>

                <div className="info-row">
                  <span className="info-label">City / Town:</span>
                  <span className="info-value">
                    {isEditing ? (
                      <input type="text" value={formData.personalInfo.city} onChange={(e) => handleInputChange('personalInfo', 'city', e.target.value)} style={{ width: '100%' }} placeholder="e.g. Nakuru" />
                    ) : (member.personalInfo?.city || 'N/A')}
                  </span>
                </div>

                <div className="info-row">
                  <span className="info-label">Home County:</span>
                  <span className="info-value">
                    {isEditing ? (
                      <input type="text" value={formData.personalInfo.homeCounty} onChange={(e) => handleInputChange('personalInfo', 'homeCounty', e.target.value)} style={{ width: '100%' }} placeholder="e.g. Nakuru" />
                    ) : (member.personalInfo?.homeCounty || 'N/A')}
                  </span>
                </div>

                <div className="info-row">
                  <span className="info-label">Employment Status:</span>
                  <span className="info-value">
                    {isEditing ? (
                      <select
                        value={formData.personalInfo.employmentStatus}
                        onChange={(e) => handleInputChange('personalInfo', 'employmentStatus', e.target.value)}
                        style={{ width: '100%' }}
                      >
                        <option value="">Select Status</option>
                        <option value="Employed">Employed</option>
                        <option value="Self-Employed">Self-Employed</option>
                        <option value="Unemployed">Unemployed</option>
                        <option value="Retired">Retired</option>
                      </select>
                    ) : (
                      (member.personalInfo?.employmentStatus || 'N/A')
                    )}
                  </span>
                </div>

                <div className="info-row">
                  <span className="info-label">Employer:</span>
                  <span className="info-value">
                    {isEditing ? (
                      <input
                        type="text"
                        value={formData.personalInfo.employerName}
                        onChange={(e) => handleInputChange('personalInfo', 'employerName', e.target.value)}
                        style={{ width: '100%' }}
                      />
                    ) : (
                      (member.personalInfo?.employerName || 'N/A')
                    )}
                  </span>
                </div>
              </div>

              {isEditing && (
                <div style={{ marginTop: '0.75rem', color: '#666', fontSize: '0.9rem' }}>
                  Note: You can update your profile <strong>only once</strong>. After saving, editing will be locked. Full Name and Member ID cannot be changed.
                </div>
              )}
            </div>

            {/* SACCO Information */}
            <div className="info-section">
              <h3>SACCO Information</h3>
              <div className="info-grid">
                <div className="info-row">
                  <span className="info-label">Member ID:</span>
                  {/* ❌ memberId never editable */}
                  <span className="info-value">{member.memberId}</span>
                </div>
                <div className="info-row">
                  <span className="info-label">Date Joined:</span>
                  <span className="info-value">{formatDate(member.saccoInfo?.dateJoined)}</span>
                </div>
                <div className="info-row">
                  <span className="info-label">Membership Type:</span>
                  <span className="info-value">{member.saccoInfo?.membershipType || 'Regular'}</span>
                </div>
                <div className="info-row">
                  <span className="info-label">Status:</span>
                  <span className={`status-badge status-${member.saccoInfo?.status?.toLowerCase()}`}>
                    {member.saccoInfo?.status || 'N/A'}
                  </span>
                </div>
                <div className="info-row">
                  <span className="info-label">Initial Shares:</span>
                  <span className="info-value">KES {(member.saccoInfo?.initialShares || 0).toLocaleString()}</span>
                </div>
                <div className="info-row">
                  <span className="info-label">Monthly Commitment:</span>
                  <span className="info-value">KES {(member.saccoInfo?.monthlyShareCommitment || 0).toLocaleString()}</span>
                </div>
                <div className="info-row">
                  <span className="info-label">Department:</span>
                  <span className="info-value">{member.saccoInfo?.department || 'N/A'}</span>
                </div>
                <div className="info-row">
                  <span className="info-label">Payroll Number:</span>
                  <span className="info-value">{member.saccoInfo?.payrollNumber || 'N/A'}</span>
                </div>
              </div>
            </div>

            {/* Next of Kin */}
            <div className="info-section">
              <h3>Next of Kin</h3>
              <div className="info-grid">
                <div className="info-row">
                  <span className="info-label">Name:</span>
                  <span className="info-value">
                    {isEditing ? (
                      <input
                        type="text"
                        value={formData.nextOfKin.name}
                        onChange={(e) => handleInputChange('nextOfKin', 'name', e.target.value)}
                        style={{ width: '100%' }}
                      />
                    ) : (
                      (member.nextOfKin?.name || 'N/A')
                    )}
                  </span>
                </div>
                <div className="info-row">
                  <span className="info-label">Relationship:</span>
                  <span className="info-value">
                    {isEditing ? (
                      <select
                        value={formData.nextOfKin.relationship}
                        onChange={(e) => handleInputChange('nextOfKin', 'relationship', e.target.value)}
                        style={{ width: '100%' }}
                      >
                        <option value="">Select Relationship</option>
                        <option value="Spouse">Spouse</option>
                        <option value="Parent">Parent</option>
                        <option value="Sibling">Sibling</option>
                        <option value="Child">Child</option>
                        <option value="Other">Other</option>
                      </select>
                    ) : (
                      (member.nextOfKin?.relationship || 'N/A')
                    )}
                  </span>
                </div>
                <div className="info-row">
                  <span className="info-label">Phone Number:</span>
                  <span className="info-value">
                    {isEditing ? (
                      <input
                        type="tel"
                        value={formData.nextOfKin.phoneNumber}
                        onChange={(e) => handleInputChange('nextOfKin', 'phoneNumber', e.target.value)}
                        style={{ width: '100%' }}
                      />
                    ) : (
                      (member.nextOfKin?.phoneNumber || 'N/A')
                    )}
                  </span>
                </div>
                <div className="info-row">
                  <span className="info-label">ID Number:</span>
                  <span className="info-value">
                    {isEditing ? (
                      <input
                        type="text"
                        value={formData.nextOfKin.idNumber}
                        onChange={(e) => handleInputChange('nextOfKin', 'idNumber', e.target.value)}
                        style={{ width: '100%' }}
                      />
                    ) : (
                      (member.nextOfKin?.idNumber || 'N/A')
                    )}
                  </span>
                </div>
                <div className="info-row">
                  <span className="info-label">Address:</span>
                  <span className="info-value">
                    {isEditing ? (
                      <input
                        type="text"
                        value={formData.nextOfKin.physicalAddress}
                        onChange={(e) => handleInputChange('nextOfKin', 'physicalAddress', e.target.value)}
                        style={{ width: '100%' }}
                      />
                    ) : (
                      (member.nextOfKin?.physicalAddress || 'N/A')
                    )}
                  </span>
                </div>
                <div className="info-row">
                  <span className="info-label">Benefit %:</span>
                  <span className="info-value">
                    {isEditing ? (
                      <input
                        type="number"
                        min="0"
                        max="100"
                        value={formData.nextOfKin.benefitPercentage}
                        onChange={(e) => handleInputChange('nextOfKin', 'benefitPercentage', e.target.value)}
                        placeholder="e.g. 50"
                        style={{ width: '100%' }}
                      />
                    ) : (
                      member.nextOfKin?.benefitPercentage !== undefined && member.nextOfKin?.benefitPercentage !== ''
                        ? `${member.nextOfKin.benefitPercentage}%`
                        : 'N/A'
                    )}
                  </span>
                </div>
              </div>

              {/* Other Next of Kin */}
              <div style={{ marginTop: 16 }}>
                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 8 }}>
                  <strong style={{ fontSize: 14 }}>Other Beneficiaries</strong>
                  {isEditing && (
                    <button type="button" className="btn btn-secondary" onClick={addOtherKin} style={{ fontSize: 12, padding: '4px 10px' }}>
                      + Add
                    </button>
                  )}
                </div>

                {!isEditing && (!Array.isArray(member.otherNextOfKin) || member.otherNextOfKin.length === 0) && (
                  <p style={{ color: '#888', fontSize: 13, margin: 0 }}>None recorded.</p>
                )}

                {!isEditing && Array.isArray(member.otherNextOfKin) && member.otherNextOfKin.map((k, i) => (
                  <div key={i} style={{ paddingLeft: 8, borderLeft: '3px solid #e0e0e0', marginBottom: 6 }}>
                    <span style={{ fontSize: 14 }}>
                      {k.name} — {k.relationship || '—'}
                      {k.benefitPercentage !== '' && k.benefitPercentage !== undefined ? ` (${k.benefitPercentage}%)` : ''}
                    </span>
                  </div>
                ))}

                {isEditing && otherNextOfKin.map((kin, idx) => (
                  <div key={idx} style={{ border: '1px solid #ddd', borderRadius: 6, padding: '10px 14px', marginBottom: 8, background: '#fafafa' }}>
                    <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: 6 }}>
                      <strong style={{ fontSize: 13 }}>Beneficiary {idx + 1}</strong>
                      <button type="button" onClick={() => removeOtherKin(idx)} style={{ background: 'none', border: 'none', color: '#e53935', cursor: 'pointer', fontSize: 13 }}>✕ Remove</button>
                    </div>
                    <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr 80px', gap: 8 }}>
                      <div>
                        <label style={{ fontSize: 12, color: '#666' }}>Name</label>
                        <input type="text" value={kin.name} onChange={(e) => handleOtherKinChange(idx, 'name', e.target.value)} style={{ width: '100%' }} placeholder="Full name" />
                      </div>
                      <div>
                        <label style={{ fontSize: 12, color: '#666' }}>Relationship</label>
                        <select value={kin.relationship} onChange={(e) => handleOtherKinChange(idx, 'relationship', e.target.value)} style={{ width: '100%' }}>
                          <option value="">Select</option>
                          <option value="Spouse">Spouse</option>
                          <option value="Parent">Parent</option>
                          <option value="Child">Child</option>
                          <option value="Sibling">Sibling</option>
                          <option value="Other">Other</option>
                        </select>
                      </div>
                      <div>
                        <label style={{ fontSize: 12, color: '#666' }}>Benefit %</label>
                        <input type="number" min="0" max="100" value={kin.benefitPercentage} onChange={(e) => handleOtherKinChange(idx, 'benefitPercentage', e.target.value)} style={{ width: '100%' }} placeholder="%" />
                      </div>
                    </div>
                  </div>
                ))}

                {isEditing && otherNextOfKin.length === 0 && (
                  <p style={{ color: '#888', fontSize: 13, margin: 0 }}>None added.</p>
                )}
              </div>
            </div>
          </div>
        </div>

        {/* Action Button */}
        <div style={{ marginTop: '2rem', textAlign: 'center' }}>
          <button
            className="btn btn-secondary"
            onClick={() => navigate('/member/dashboard')}
          >
            ← Back to Dashboard
          </button>
        </div>
      </div>
    </MemberLayout>
  );
};

export default MyProfilePage;
